import {
  Component,
  EventEmitter,
  HostListener,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  SimpleChanges,
} from '@angular/core';
import { Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { GlobalStoreService } from '../../../../global/services/global-store.service';
import { Project } from '../../../../features/projects/interfaces/project.interface';
import { Career, Contact, Expertise, News } from '../../../interfaces/shared-interface';
import { SearchItem } from '../../../interfaces/search.interface';
import { resolveAppMediaSource } from '../../../utils/app-media-url.utils';

interface SearchGroup {
  key: SearchItem['type'];
  label: string;
  items: SearchItem[];
}

@Component({
  selector: 'app-site-search',
  templateUrl: './site-search.component.html',
  styleUrl: './site-search.component.scss',
  standalone: false,
})
export class SiteSearchComponent implements OnChanges, OnDestroy {
  @Input() isOpen = false;
  @Output() closed = new EventEmitter<void>();

  public searchQuery = '';
  public filteredSearchResults: SearchItem[] = [];
  public groupedSearchResults: SearchGroup[] = [];

  private unsubscribe$ = new Subject<void>();
  private failedImageIds = new Set<string>();
  private searchIndex: SearchItem[] = [];
  private allProjects: Project[] = [];
  private allExpertises: Expertise[] = [];
  private allNews: News[] = [];
  private allCareers: Career[] = [];
  private allContacts: Contact[] = [];

  constructor(
    private globalStore: GlobalStoreService,
    private router: Router
  ) {
    this.subscribeSearchDataSources();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['isOpen']) {
      return;
    }

    if (this.isOpen) {
      this.ensureSearchDataLoaded();
      this.searchQuery = '';
      this.updateVisibleResults('');
      return;
    }

    this.searchQuery = '';
    this.filteredSearchResults = [];
    this.groupedSearchResults = [];
  }

  public closeSearch(): void {
    this.closed.emit();
  }

  public onSearchQueryChange(value: string): void {
    this.searchQuery = value;
    this.updateVisibleResults(value);
  }

  public navigateFromSearch(item: SearchItem): void {
    this.closeSearch();
    this.router.navigateByUrl(item.route);
  }

  public onImageError(itemId: string): void {
    this.failedImageIds.add(itemId);
  }

  public hasRenderableImage(item: SearchItem): boolean {
    return !!item.imageUrl && !this.failedImageIds.has(item.id);
  }

  public getSearchTypeLabel(type: SearchItem['type']): string {
    const labels: Record<SearchItem['type'], string> = {
      project: 'Project',
      expertise: 'Expertise',
      news: 'News',
      career: 'Career',
      contact: 'Location',
    };

    return labels[type];
  }

  @HostListener('document:keydown.escape')
  public onEscapeKey(): void {
    if (this.isOpen) {
      this.closeSearch();
    }
  }

  private subscribeSearchDataSources(): void {
    this.globalStore
      .selectAllProjects()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((projects) => {
        this.allProjects = projects || [];
        this.buildSearchIndex();
      });

    this.globalStore
      .selectAllExpertises()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((expertises) => {
        this.allExpertises = expertises || [];
        this.buildSearchIndex();
      });

    this.globalStore
      .selectAllNews()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((news) => {
        this.allNews = news || [];
        this.buildSearchIndex();
      });

    this.globalStore
      .selectAllCareers()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((careers) => {
        this.allCareers = careers || [];
        this.buildSearchIndex();
      });

    this.globalStore
      .selectContacts()
      .pipe(takeUntil(this.unsubscribe$))
      .subscribe((contacts) => {
        this.allContacts = contacts || [];
        this.buildSearchIndex();
      });
  }

  private ensureSearchDataLoaded(): void {
    if (!this.allProjects.length) {
      this.globalStore.fetchAllProjects();
    }

    if (!this.allExpertises.length) {
      this.globalStore.fetchAllExpertises();
    }

    if (!this.allNews.length) {
      this.globalStore.fetchAllNews();
    }

    if (!this.allCareers.length) {
      this.globalStore.fetchAllCareers();
    }

    if (!this.allContacts.length) {
      this.globalStore.fetchContacts();
    }
  }

  private buildSearchIndex(): void {
    const recentProjects = [...this.allProjects].sort((a, b) => (b.id || 0) - (a.id || 0));
    const recentNews = [...this.allNews].sort((a, b) => {
      const dateA = new Date(a.publishedAt || a.updatedAt || 0).getTime();
      const dateB = new Date(b.publishedAt || b.updatedAt || 0).getTime();

      if (dateB !== dateA) {
        return dateB - dateA;
      }

      return (b.id || 0) - (a.id || 0);
    });
    const recentExpertises = [...this.allExpertises].sort((a, b) => (b.id || 0) - (a.id || 0));

    const projectItems: SearchItem[] = recentProjects.map((item) => ({
      id: `project-${item.id}`,
      title: item.title,
      subtitle: item.location || item.status || 'Project',
      type: 'project',
      route: `/projects/project/${item.id}/${item.title}`,
      imageUrl: this.buildAssetUrl(item.imageName),
      keywords: this.toSearchText([
        item.title,
        item.description,
        item.location,
        item.status,
        item.type,
        item.expertise,
      ]),
    }));

    const expertiseItems: SearchItem[] = recentExpertises.map((item) => ({
      id: `expertise-${item.id}`,
      title: item.name,
      subtitle: item.description || 'Expertise',
      type: 'expertise',
      route: `/expertises/expertise/${item.id}/${item.name}`,
      imageUrl: this.buildAssetUrl(item.featureImage || item.headerFile),
      keywords: this.toSearchText([item.name, item.description]),
    }));

    const newsItems: SearchItem[] = recentNews.map((item) => ({
      id: `news-${item.id}`,
      title: item.title,
      subtitle: item.category || item.subTitle || 'News',
      type: 'news',
      route: `/news/articles/${item.id}/${item.title}`,
      imageUrl: this.buildAssetUrl(item.fileName),
      keywords: this.toSearchText([
        item.title,
        item.subTitle,
        item.category,
        item.readMore,
      ]),
    }));

    const careerItems: SearchItem[] = this.allCareers.map((item) => ({
      id: `career-${item.id}`,
      title: item.position,
      subtitle: item.departmentName || `${item.city}, ${item.country}`,
      type: 'career',
      route: `/careers/career/${item.id}/${item.position}`,
      imageUrl: this.buildAssetUrl(item.fileName),
      keywords: this.toSearchText([
        item.position,
        item.departmentName,
        item.city,
        item.country,
        item.description,
        item.requirements,
      ]),
    }));

    const contactItems: SearchItem[] = this.allContacts.map((item) => ({
      id: `contact-${item.id}`,
      title: item.residenceName || item.location,
      subtitle: item.location || item.descriptionAddress || 'Location',
      type: 'contact',
      route: '/contact',
      keywords: this.toSearchText([
        item.residenceName,
        item.location,
        item.descriptionAddress,
        item.companyEmail,
      ]),
    }));

    this.searchIndex = [
      ...projectItems,
      ...newsItems,
      ...expertiseItems,
      ...careerItems,
      ...contactItems,
    ];

    if (this.isOpen) {
      this.updateVisibleResults(this.searchQuery);
    }
  }

  private updateVisibleResults(queryValue: string): void {
    const query = queryValue.trim().toLowerCase();

    if (!query) {
      this.filteredSearchResults = this.searchIndex;
      this.groupedSearchResults = this.buildGroups(this.filteredSearchResults);
      return;
    }

    this.filteredSearchResults = this.searchIndex
      .filter((item) => item.keywords.includes(query));
    this.groupedSearchResults = this.buildGroups(this.filteredSearchResults);
  }

  private buildGroups(items: SearchItem[]): SearchGroup[] {
    const limitedTypes = new Set<SearchItem['type']>(['project', 'news', 'expertise']);
    const groupOrder: Array<{ key: SearchItem['type']; label: string }> = [
      { key: 'project', label: 'Projects' },
      { key: 'news', label: 'News' },
      { key: 'career', label: 'Careers' },
      { key: 'expertise', label: 'Expertises' },
      { key: 'contact', label: 'Locations' },
    ];

    return groupOrder
      .map((group) => ({
        key: group.key,
        label: group.label,
        items: limitedTypes.has(group.key)
          ? items.filter((item) => item.type === group.key).slice(0, 3)
          : items.filter((item) => item.type === group.key),
      }))
      .filter((group) => group.items.length > 0);
  }

  private toSearchText(values: Array<string | undefined | null>): string {
    return values
      .filter((value): value is string => !!value)
      .join(' ')
      .toLowerCase();
  }

  private buildAssetUrl(fileName?: string | null): string | undefined {
    if (!fileName) {
      return undefined;
    }

    return resolveAppMediaSource(fileName, 'image');
  }

  ngOnDestroy(): void {
    this.unsubscribe$.next();
    this.unsubscribe$.complete();
  }
}
